import { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { MapPin, Building2, Users, FileText, Target, AlertTriangle, Filter, X, ChevronDown } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fix default marker icon issue with webpack/react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const SCORE_COLORS = {
  excellent: '#10B981',
  good: '#B8860B',
  average: '#F59E0B',
  attention: '#EF4444',
  none: '#9CA3AF',
};

const PLAN_LABELS = {
  diagnostic: 'Diagnostic',
  pilotage: 'Pilotage',
  premium: 'Premium',
  '': 'Aucun',
};

function scoreCategory(score) {
  if (score === null || score === undefined) return 'none';
  if (score >= 70) return 'excellent';
  if (score >= 50) return 'good';
  if (score >= 30) return 'average';
  return 'attention';
}

function createMarkerIcon(score) {
  const cat = scoreCategory(score);
  const color = SCORE_COLORS[cat];
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
    <path d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.268 21.732 0 14 0z" fill="${color}" stroke="white" stroke-width="2"/>
    <circle cx="14" cy="14" r="6" fill="white"/>
    ${score !== null && score !== undefined ? `<text x="14" y="17" text-anchor="middle" font-size="8" font-weight="bold" fill="${color}">${score}</text>` : `<circle cx="14" cy="14" r="3" fill="${color}"/>`}
  </svg>`;
  return L.divIcon({
    html: svg,
    className: 'custom-marker',
    iconSize: [28, 40],
    iconAnchor: [14, 40],
    popupAnchor: [0, -40],
  });
}

function FitBounds({ positions }) {
  const map = useMap();
  useEffect(() => {
    if (positions.length > 0) {
      const bounds = L.latLngBounds(positions);
      map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
    }
  }, [positions, map]);
  return null;
}

function StatCard({ icon: Icon, label, value, sub, color }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 hover:shadow-md transition-all" data-testid={`carto-stat-${label.toLowerCase().replace(/[\s']+/g, '-')}`}>
      <div className="flex items-center justify-between mb-2">
        <Icon size={16} style={{ color }} className="opacity-60" />
        <span className="text-[9px] font-bold text-gray-300 uppercase tracking-wider">{label}</span>
      </div>
      <p className="text-2xl font-bold text-[#1B2A4A] font-manrope">{value}</p>
      {sub && <p className="text-[10px] text-gray-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function EstablishmentCard({ est, isSelected, onClick }) {
  const cat = scoreCategory(est.score);
  const color = SCORE_COLORS[cat];
  return (
    <button
      onClick={onClick}
      className={`w-full text-left p-3 rounded-lg border transition-all ${isSelected ? 'border-[#B8860B] bg-[#B8860B]/5 shadow-sm' : 'border-gray-100 hover:border-gray-200 hover:bg-gray-50/50'}`}
      data-testid={`carto-est-${est.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#1B2A4A] truncate">{est.name}</p>
          <p className="text-[10px] text-gray-400 truncate">{est.city || est.adresse_complete || 'Adresse non renseignée'}</p>
        </div>
        {est.score !== null && est.score !== undefined ? (
          <div className="flex items-center gap-1 shrink-0">
            <span className="text-sm font-bold font-manrope" style={{ color }}>{est.score}</span>
            <span className="text-[9px] text-gray-300">/100</span>
          </div>
        ) : (
          <span className="text-[9px] text-gray-300 bg-gray-50 px-1.5 py-0.5 rounded">N/A</span>
        )}
      </div>
      <div className="flex items-center gap-3 mt-1.5">
        <span className="text-[9px] text-gray-400">{est.sector_name || '—'}</span>
        {est.plan && <span className="text-[9px] font-medium text-[#B8860B]">{PLAN_LABELS[est.plan] || est.plan}</span>}
        <span className="text-[9px] text-gray-400">{est.report_count} rapport{est.report_count !== 1 ? 's' : ''}</span>
      </div>
    </button>
  );
}

export default function CartographyPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [sectorFilter, setSectorFilter] = useState('');
  const [planFilter, setPlanFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('benchory_token');
    fetch(`${API_URL}/admin/cartography`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const allEstablishments = useMemo(() => {
    if (!data) return [];
    return [...data.features, ...data.missing_coords];
  }, [data]);

  const filtered = useMemo(() => {
    return allEstablishments.filter(e => {
      if (sectorFilter && e.sector_name !== sectorFilter) return false;
      if (planFilter && e.plan !== planFilter) return false;
      return true;
    });
  }, [allEstablishments, sectorFilter, planFilter]);

  const mappedFiltered = useMemo(() => {
    return filtered.filter(e => e.latitude && e.longitude && e.latitude !== 0 && e.longitude !== 0);
  }, [filtered]);

  const positions = useMemo(() => {
    return mappedFiltered.map(e => [e.latitude, e.longitude]);
  }, [mappedFiltered]);

  const selectedEst = useMemo(() => {
    if (!selectedId) return null;
    return allEstablishments.find(e => e.id === selectedId);
  }, [selectedId, allEstablishments]);

  const sectors = useMemo(() => {
    const s = new Set(allEstablishments.map(e => e.sector_name).filter(Boolean));
    return Array.from(s).sort();
  }, [allEstablishments]);

  const plans = useMemo(() => {
    const p = new Set(allEstablishments.map(e => e.plan).filter(Boolean));
    return Array.from(p).sort();
  }, [allEstablishments]);

  if (loading) return (
    <div className="flex items-center justify-center h-64" data-testid="carto-loading">
      <div className="w-8 h-8 border-3 border-[#1B2A4A]/20 border-t-[#1B2A4A] rounded-full animate-spin" />
    </div>
  );

  if (!data) return (
    <div className="text-center py-20 text-gray-400" data-testid="carto-error">Erreur de chargement des données</div>
  );

  const defaultCenter = positions.length > 0
    ? [positions.reduce((s, p) => s + p[0], 0) / positions.length, positions.reduce((s, p) => s + p[1], 0) / positions.length]
    : [46.603354, 1.888334]; // France center

  return (
    <div className="space-y-6" data-testid="cartography-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold text-[#1B2A4A] font-manrope tracking-tight">Cartographie</h1>
          <p className="text-gray-400 mt-1 text-sm">
            {data.stats.mapped} établissement{data.stats.mapped > 1 ? 's' : ''} géolocalisé{data.stats.mapped > 1 ? 's' : ''} sur {data.stats.total}
          </p>
        </div>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium border transition-all shadow-sm ${showFilters ? 'bg-[#1B2A4A] text-white border-[#1B2A4A]' : 'bg-white text-[#1B2A4A] border-gray-200 hover:border-[#B8860B]/40'}`}
          data-testid="carto-filter-toggle"
        >
          <Filter size={14} />
          Filtres
          {(sectorFilter || planFilter) && (
            <span className="bg-[#B8860B] text-white text-[9px] font-bold rounded-full w-4 h-4 flex items-center justify-center">
              {(sectorFilter ? 1 : 0) + (planFilter ? 1 : 0)}
            </span>
          )}
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 flex flex-wrap items-center gap-4" data-testid="carto-filters">
          <div className="flex items-center gap-2">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Secteur</label>
            <select
              value={sectorFilter}
              onChange={e => setSectorFilter(e.target.value)}
              className="h-8 rounded-lg border border-gray-200 px-3 text-sm text-[#1B2A4A] focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20"
              data-testid="carto-filter-sector"
            >
              <option value="">Tous</option>
              {sectors.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="flex items-center gap-2">
            <label className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Plan</label>
            <select
              value={planFilter}
              onChange={e => setPlanFilter(e.target.value)}
              className="h-8 rounded-lg border border-gray-200 px-3 text-sm text-[#1B2A4A] focus:outline-none focus:ring-2 focus:ring-[#1B2A4A]/20"
              data-testid="carto-filter-plan"
            >
              <option value="">Tous</option>
              {plans.map(p => <option key={p} value={p}>{PLAN_LABELS[p] || p}</option>)}
            </select>
          </div>
          {(sectorFilter || planFilter) && (
            <button
              onClick={() => { setSectorFilter(''); setPlanFilter(''); }}
              className="flex items-center gap-1 text-xs text-red-500 hover:text-red-700 font-medium transition-colors"
              data-testid="carto-clear-filters"
            >
              <X size={12} /> Réinitialiser
            </button>
          )}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="carto-stats">
        <StatCard icon={Building2} label="Total" value={data.stats.total} sub="établissements" color="#1B2A4A" />
        <StatCard icon={MapPin} label="Géolocalisés" value={data.stats.mapped} sub={`${data.stats.unmapped} sans coordonnées`} color="#10B981" />
        <StatCard icon={Users} label="Secteurs" value={Object.keys(data.stats.by_sector).length} color="#B8860B" />
        <StatCard icon={FileText} label="Avec rapports" value={allEstablishments.filter(e => e.report_count > 0).length} color="#1B93A4" />
      </div>

      {/* Map + Sidebar */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Map */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden" data-testid="carto-map-container">
          <div className="h-[500px] relative">
            {mappedFiltered.length > 0 ? (
              <MapContainer
                center={defaultCenter}
                zoom={6}
                className="h-full w-full z-0"
                scrollWheelZoom={true}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FitBounds positions={positions} />
                {mappedFiltered.map(est => (
                  <Marker
                    key={est.id}
                    position={[est.latitude, est.longitude]}
                    icon={createMarkerIcon(est.score)}
                    eventHandlers={{ click: () => setSelectedId(est.id) }}
                  >
                    <Popup>
                      <div className="min-w-[200px]">
                        <p className="font-bold text-[#1B2A4A] text-sm mb-1">{est.name}</p>
                        <p className="text-xs text-gray-500 mb-2">{est.adresse_complete || est.city}</p>
                        <div className="space-y-1 text-xs">
                          <div className="flex justify-between">
                            <span className="text-gray-400">Secteur</span>
                            <span className="font-medium text-[#1B2A4A]">{est.sector_name || '—'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-400">Plan</span>
                            <span className="font-medium text-[#B8860B]">{PLAN_LABELS[est.plan] || '—'}</span>
                          </div>
                          {est.score !== null && est.score !== undefined && (
                            <div className="flex justify-between">
                              <span className="text-gray-400">Score</span>
                              <span className="font-bold" style={{ color: SCORE_COLORS[scoreCategory(est.score)] }}>{est.score}/100</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className="text-gray-400">Rapports</span>
                            <span className="font-medium">{est.report_count}</span>
                          </div>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            ) : (
              <div className="h-full flex flex-col items-center justify-center text-gray-400">
                <MapPin size={48} className="mb-4 opacity-30" />
                <p className="text-sm font-medium">Aucun établissement géolocalisé</p>
                <p className="text-xs mt-1">Les coordonnées GPS sont capturées lors de l'inscription</p>
              </div>
            )}
          </div>
          {/* Legend */}
          <div className="px-4 py-3 border-t border-gray-100 flex flex-wrap gap-4" data-testid="carto-legend">
            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider">Légende :</span>
            {[
              { label: 'Excellent (70+)', color: SCORE_COLORS.excellent },
              { label: 'Bon (50-69)', color: SCORE_COLORS.good },
              { label: 'Moyen (30-49)', color: SCORE_COLORS.average },
              { label: 'Attention (<30)', color: SCORE_COLORS.attention },
              { label: 'Non évalué', color: SCORE_COLORS.none },
            ].map(l => (
              <span key={l.label} className="flex items-center gap-1.5 text-[10px] text-gray-500">
                <span className="w-3 h-3 rounded-full" style={{ backgroundColor: l.color }} />
                {l.label}
              </span>
            ))}
          </div>
        </div>

        {/* Sidebar */}
        <div className="bg-white rounded-xl border border-gray-100 shadow-sm flex flex-col" data-testid="carto-sidebar">
          <div className="px-4 py-3 border-b border-gray-100">
            <h2 className="text-sm font-bold text-[#1B2A4A] font-manrope">
              Établissements ({filtered.length})
            </h2>
          </div>

          {/* Selected establishment detail */}
          {selectedEst && (
            <div className="px-4 py-4 border-b border-gray-100 bg-[#1B2A4A]/[0.02]" data-testid="carto-selected-detail">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <p className="text-sm font-bold text-[#1B2A4A]">{selectedEst.name}</p>
                  <p className="text-[10px] text-gray-400">{selectedEst.adresse_complete || selectedEst.city || '—'}</p>
                </div>
                <button onClick={() => setSelectedId(null)} className="p-1 text-gray-300 hover:text-gray-500">
                  <X size={14} />
                </button>
              </div>
              <div className="grid grid-cols-2 gap-2 text-[11px]">
                <div className="bg-white rounded-lg p-2 border border-gray-100">
                  <p className="text-gray-400 text-[9px] uppercase font-bold tracking-wider">Secteur</p>
                  <p className="font-semibold text-[#1B2A4A] mt-0.5">{selectedEst.sector_name || '—'}</p>
                </div>
                <div className="bg-white rounded-lg p-2 border border-gray-100">
                  <p className="text-gray-400 text-[9px] uppercase font-bold tracking-wider">Plan</p>
                  <p className="font-semibold text-[#B8860B] mt-0.5">{PLAN_LABELS[selectedEst.plan] || '—'}</p>
                </div>
                <div className="bg-white rounded-lg p-2 border border-gray-100">
                  <p className="text-gray-400 text-[9px] uppercase font-bold tracking-wider">Score</p>
                  <p className="font-bold mt-0.5" style={{ color: SCORE_COLORS[scoreCategory(selectedEst.score)] }}>
                    {selectedEst.score !== null && selectedEst.score !== undefined ? `${selectedEst.score}/100` : 'N/A'}
                  </p>
                </div>
                <div className="bg-white rounded-lg p-2 border border-gray-100">
                  <p className="text-gray-400 text-[9px] uppercase font-bold tracking-wider">Rapports</p>
                  <p className="font-semibold text-[#1B2A4A] mt-0.5">{selectedEst.report_count}</p>
                </div>
                {selectedEst.owner_prenom && (
                  <div className="col-span-2 bg-white rounded-lg p-2 border border-gray-100">
                    <p className="text-gray-400 text-[9px] uppercase font-bold tracking-wider">Contact</p>
                    <p className="font-semibold text-[#1B2A4A] mt-0.5">{selectedEst.owner_prenom} {selectedEst.owner_nom}</p>
                    <p className="text-[10px] text-gray-400">{selectedEst.owner_email}</p>
                  </div>
                )}
                {selectedEst.mode_gestion && (
                  <div className="bg-white rounded-lg p-2 border border-gray-100">
                    <p className="text-gray-400 text-[9px] uppercase font-bold tracking-wider">Gestion</p>
                    <p className="font-semibold text-[#1B2A4A] mt-0.5 capitalize">{selectedEst.mode_gestion}</p>
                  </div>
                )}
                {selectedEst.opening_hours_week > 0 && (
                  <div className="bg-white rounded-lg p-2 border border-gray-100">
                    <p className="text-gray-400 text-[9px] uppercase font-bold tracking-wider">Heures/sem</p>
                    <p className="font-semibold text-[#1B2A4A] mt-0.5">{selectedEst.opening_hours_week}h</p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Establishment list */}
          <div className="flex-1 overflow-y-auto max-h-[400px] p-2 space-y-1" data-testid="carto-est-list">
            {filtered.map(est => (
              <EstablishmentCard
                key={est.id}
                est={est}
                isSelected={selectedId === est.id}
                onClick={() => setSelectedId(est.id === selectedId ? null : est.id)}
              />
            ))}
            {filtered.length === 0 && (
              <div className="text-center py-8 text-gray-400 text-sm">
                Aucun établissement ne correspond aux filtres
              </div>
            )}
          </div>

          {/* Unmapped warning */}
          {data.stats.unmapped > 0 && (
            <div className="px-4 py-3 border-t border-gray-100 bg-amber-50/50" data-testid="carto-unmapped-warning">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="text-amber-500 mt-0.5 shrink-0" />
                <div>
                  <p className="text-[11px] font-semibold text-amber-700">
                    {data.stats.unmapped} établissement{data.stats.unmapped > 1 ? 's' : ''} sans coordonnées
                  </p>
                  <p className="text-[10px] text-amber-600/70 mt-0.5">
                    L'adresse GPS est capturée automatiquement lors de l'inscription
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
